/**
 * useMapEffects — Coordinates all side-effects for MapboxMap:
 * layer updates, basemap changes, flyTo and POI fetch triggers.
 */
import { useRef, useEffect, useCallback } from 'react';

export default function useMapEffects({
  mapRef,
  mapLoaded,
  iconsLoaded,
  setIconsLoaded,
  // Layer updaters
  loadIcons,
  updateLayers,
  updatePOILayer,
  updateWaterwaysLayer,
  // Data
  destinationsGeoJSON,
  poisGeoJSON,
  // Toggles
  showStreamGauges,
  showPOIs,
  showRivers,
  showLakes,
  selectedPOITypes,
  // Viewport data
  fetchViewportData,
  setViewportPOIs,
  lastPOITypesRef,
  // Fly to
  flyToLocation,
  // Bounds
  onBoundsChange,
  handleMoveEnd,
}) {
  const isInitialLoadRef = useRef(true);
  const currentBasemapRef = useRef(null);
  const lastFlyToRef = useRef(null);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      loadIcons(map);

      const bounds = map.getBounds();
      const zoom = map.getZoom();
      const center = map.getCenter();

      if (onBoundsChange) {
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
          zoom: zoom,
          center: { lat: center.lat, lng: center.lng }
        });
      }

      fetchViewportData(bounds, zoom);
    }
  }, [mapRef, loadIcons, onBoundsChange, fetchViewportData]);

  // Update layers when data changes
  useEffect(() => {
    if (mapRef.current && mapLoaded && iconsLoaded) {
      const map = mapRef.current.getMap();
      const showPOILayer = showPOIs || showStreamGauges;
      if (!map.isStyleLoaded()) {
        const onStyleReady = () => {
          updateLayers(map, destinationsGeoJSON, false);
          updatePOILayer(map, poisGeoJSON, showPOILayer, false);
          updateWaterwaysLayer(map, showRivers, showLakes, false);
        };
        map.once('style.load', onStyleReady);
        setTimeout(() => {
          if (map.isStyleLoaded()) {
            onStyleReady();
          }
        }, 200);
        return () => map.off('style.load', onStyleReady);
      }
      updateLayers(map, destinationsGeoJSON, false);
      updatePOILayer(map, poisGeoJSON, showPOILayer, false);
      updateWaterwaysLayer(map, showRivers, showLakes, false);
    }
  }, [mapRef, mapLoaded, iconsLoaded, destinationsGeoJSON, showStreamGauges, poisGeoJSON, showPOIs, showRivers, showLakes, updateLayers, updatePOILayer, updateWaterwaysLayer]);

  // Separate effect for waterways toggle to ensure immediate response
  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      const map = mapRef.current.getMap();
      updateWaterwaysLayer(map, showRivers, showLakes, false);
    }
  }, [mapRef, mapLoaded, showRivers, showLakes, updateWaterwaysLayer]);

  // Effect to fetch POIs when POI settings or stream gauge toggle changes
  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      const zoom = map.getZoom();

      const shouldFetch = (showPOIs && selectedPOITypes.length > 0) || showStreamGauges;

      if (zoom >= 6 && shouldFetch) {
        lastPOITypesRef.current = null;
        fetchViewportData(bounds, zoom);
      } else if (!shouldFetch) {
        setViewportPOIs([]);
      }
    }
  }, [mapRef, mapLoaded, showPOIs, showStreamGauges, selectedPOITypes, fetchViewportData, setViewportPOIs, lastPOITypesRef]);

  // Handle style.load event for basemap changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    const map = mapRef.current.getMap();

    const onStyleLoad = async () => {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        return;
      }

      setIconsLoaded(false);

      await new Promise(resolve => requestAnimationFrame(resolve));

      if (!map.isStyleLoaded()) {
        await new Promise(resolve => {
          const checkStyle = () => {
            if (map.isStyleLoaded()) {
              resolve();
            } else {
              requestAnimationFrame(checkStyle);
            }
          };
          checkStyle();
        });
      }

      await loadIcons(map);

      const showPOILayer = showPOIs || showStreamGauges;
      updateLayers(map, destinationsGeoJSON, true);
      updatePOILayer(map, poisGeoJSON, showPOILayer, true);
      updateWaterwaysLayer(map, showRivers, showLakes, true);

    };

    map.on('style.load', onStyleLoad);

    return () => {
      map.off('style.load', onStyleLoad);
    };
  }, [mapRef, mapLoaded, loadIcons, updateLayers, updatePOILayer, updateWaterwaysLayer, destinationsGeoJSON, showStreamGauges, poisGeoJSON, showPOIs, showRivers, showLakes, setIconsLoaded]);

  // Fly to location when it changes (only once the map has loaded, or the move is lost)
  useEffect(() => {
    if (flyToLocation && mapRef.current && mapLoaded) {
      const locationKey = `${flyToLocation.latitude}-${flyToLocation.longitude}-${flyToLocation.zoom}`;

      if (lastFlyToRef.current !== locationKey) {
        lastFlyToRef.current = locationKey;
        mapRef.current.flyTo({
          center: [flyToLocation.longitude, flyToLocation.latitude],
          zoom: flyToLocation.zoom || 12,
          duration: 1500
        });
      }
    }
  }, [flyToLocation, mapRef, mapLoaded]);

  // Wrapped handleMoveEnd that injects onBoundsChange
  const onMoveEnd = useCallback(() => {
    handleMoveEnd(onBoundsChange);
  }, [handleMoveEnd, onBoundsChange]);

  return {
    handleMapLoad,
    onMoveEnd,
  };
}
